import React, { Component } from 'react';
import decode from 'jwt-decode';
import i18n from '../../i18n';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import { withSize } from 'react-sizeme';
import { Metrics, Colors } from '../../themes';
import clsx from 'clsx';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash.isequal';
import Select from '../common/Select';
import SearchImmediate from '../common/SearchImmediate';
import Typography from '@material-ui/core/Typography';
import SitesActions from '../../redux/SitesRedux';
import EventReportsActions from '../../redux/EventReportsRedux';
import WebSocketActions from '../../redux/WebSocketRedux';
import SvgIcon from '../../icons/SvgIcon';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import OpenDoorActions from '../../redux/OpenDoorRedux';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import AlertDialog from '../common/AlertDialog';
import Immutable from 'seamless-immutable';

const styles = (theme) => ({
  widgetContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  title: {
    textAlign: 'left',
    padding: theme.spacing(4),
    paddingBottom: 0,
  },
  label: {
    margin: 0,
  },
  selectorContainer: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderRadius: '4px 4px 0 0',
  },
  loader: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
  },
  body: {
    display: 'flex',
    overflow: 'hidden',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    margin: 20,
    flexWrap: 'wrap',
    overflowY: 'auto',
    marginRight: 10,
    paddingRight: 10,
    maxHeight: 233,
    '&::-webkit-scrollbar': {
      width: '0.6em',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 10,
      backgroundColor: Colors.navBackgroundGrey,
    },
  },
  bodySmall: {
    maxHeight: 170,
  },
  singleElementBody: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  door: {
    background: Colors.navBackgroundGrey,
    marginTop: 10,
    marginBottom: 10,
    padding: '0 20px',
    fontWeight: 600,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  doorNameText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusText: {
    marginLeft: 'auto',
    marginRight: 10,
  },
  doorSuccess: {
    background: Colors.blue,
  },
  doorFailure: {
    background: Colors.red,
  },
  okButton: {
    margin: 'auto 20px 20px auto',
    background: Colors.blue,
    width: 100,
    fontWeight: 600,
    '&:hover': {
      background: Colors.lightBlue,
    },
  },
  red: {
    borderRight: `5px solid ${Colors.red}`,
  },
  green: {
    borderRight: `5px solid ${Colors.successGreen}`,
  },
  yellow: {
    borderRight: `5px solid ${Colors.warningYellow}`,
  },
});

class DoorManualControl extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // alert confirm dialog
      isAlertDialogOpen: false,
      alertTitle: '',
      alertContentText: '',
      alertConfirmAction: null,
      // door manual control
      siteId: '',
      value: '',
      filteredData: [],
      doorToOpenId: '',
      controllerUid: '',
      doorSuccess: false,
      doorWait: false,
      doorFailure: false,
      timer: 12,
      doorStatuses: [],
      doorOpenTimestamp: null,
    };
    this.interval = null;
  }

  componentDidMount() {
    if (this.props.customerId) {
      this.sendGetAllSitesRequest();
    }
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    // this.props.resetSites(); not necessary as called by personCounter
    this.props.resetControllers();
    this.props.updateSitesQueues([]);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.commandId && this.props.commandId) {
      const decodedCommandId = decode(this.props.commandId);
      if (decodedCommandId?.sub !== 'null') {
        // for old controllers, poll
        this.setState({ timer: 12 });
        this.loopCheckDoorStatus();
      } else {
        // for new controllers, set timer to 15s and wait for websocket event
        this.setState({ timer: 15 });
        this.loopWaitForWebSocketEvent();
      }
    }
    // when getting controllers, update doorStatuses and clear webSocketEvents
    if (!isEqual(prevProps.controllers, this.props.controllers) && this.props.controllers.length) {
      const doorStatuses = this.props.controllers.map(({ controllerUid, name, isDoorClosed, isLockClosed }) => ({
        controllerUid,
        name,
        isDoorClosed,
        isLockClosed,
      }));
      this.setState({ doorStatuses });
      this.props.clearWebSocketEvents();
    }
    // when getting events from webSockets, update doorStatuses
    if (
      this.state.doorStatuses &&
      this.state.doorStatuses.length &&
      !isEqual(prevProps.webSocketEvents, this.props.webSocketEvents) &&
      this.props.webSocketEvents.length
    ) {
      const oldStatuses = cloneDeep(this.state.doorStatuses);
      const doorStatuses = oldStatuses.map((door) => {
        const matchingEvent = Immutable.asMutable(this.props.webSocketEvents)
          .findLast((event) => event.controllerUid === door.controllerUid);
        let { isDoorClosed, isLockClosed } = door;
        if (matchingEvent) {
          const { controllerMessageType } = matchingEvent;
          if (controllerMessageType === 'doorOpened') {
            isDoorClosed = false;
          }
          if (controllerMessageType === 'doorClosed') {
            isDoorClosed = true;
          }
          if (controllerMessageType === 'lockOpened') {
            isLockClosed = false;
          }
          if (controllerMessageType === 'lockClosed') {
            isLockClosed = true;
          }
        }
        return { ...door, isDoorClosed, isLockClosed };
      });
      this.setState({ doorStatuses });
    }
    if (prevProps.customerId !== this.props.customerId) {
      if (this.props.customerId) {
        this.sendGetAllSitesRequest();
      }
      // this.props.resetSites(); not necessary as called by personCounter
      this.props.resetControllers();
      this.resetValues();
    }
    if (prevProps.sites !== this.props.sites && this.props.sites.length === 1) {
      this.selectSite(this.props.sites[0].siteId);
    }
  }

  loopCheckDoorStatus = () => {
    this.interval = setInterval(() => {
      if (this.props.doorStatus === 202) {
        clearInterval(this.interval);
        this.props.resetStatus();
        this.setState({ doorWait: false, doorSuccess: true, timer: 12 });
      } else if (!this.props.doorStatus || this.props.doorStatus === 204) {
        this.props.checkDoorStatus(this.state.doorToOpenId, this.props.commandId);
        const timer = this.state.timer > 0 ? this.state.timer - 1 : 0;
        this.setState({ timer });
      } else if (this.props.doorStatus >= 400) {
        clearInterval(this.interval);
        this.setState({ doorWait: false, doorFailure: true, timer: 12 });
        this.props.resetStatus();
      }
    }, 1000);
  };

  loopWaitForWebSocketEvent = () => {
    this.interval = setInterval(() => {
      if (
        this.props.webSocketEvents?.find(
          (event) =>
            event.controllerUid === this.state.controllerUid &&
            event.controllerMessageType === 'remoteOpen' &&
            event.receivedAt > this.state.doorOpenTimestamp
        )
      ) {
        clearInterval(this.interval);
        this.props.resetStatus();
        this.setState({ doorWait: false, doorSuccess: true, timer: 15 });
      } else {
        // decrement timer
        const timer = this.state.timer > 0 ? this.state.timer - 1 : 0;
        if (timer > 0) {
          this.setState({ timer });
        } else {
          clearInterval(this.interval);
          this.setState({ doorWait: false, doorFailure: true, timer: 15 });
          this.props.resetStatus();
        }
      }
    }, 1000);
  };

  sendGetAllSitesRequest = () => {
    const pageParams = {
      items: -1,
      page: 0,
      value: null,
    };
    this.props.getAllSites(this.props.customerId, pageParams);
  };

  resetValues = () => {
    this.setState({ siteId: '', value: '', filteredData: [], doorToOpenId: '', controllerUid: '' });
  };

  selectSite = (siteId) => {
    this.props.resetControllers();
    this.setState({ siteId }, () => this.props.getSiteControllers(siteId));
    this.props.updateSitesQueues([siteId]);
  };

  handleChange = (value) => {
    this.setState({ value });
  };

  openOpenDoorAlert = (controllerId) => {
    const doorToOpen = this.props.controllers.find((door) => door.controllerId === controllerId);
    const doorName = doorToOpen ? (typeof doorToOpen.name !== 'undefined' ? doorToOpen.name : '') : '';
    this.setState({
      isAlertDialogOpen: true,
      alertTitle: `${i18n.t('home.openDoor')}?`,
      alertContentText: i18n.t('alert.openDoorContent', { doorName }),
      alertConfirmAction: () => this.openDoor(doorToOpen),
    });
  };

  openDoor = (doorToOpen) => {
    const { controllerId, controllerUid } = doorToOpen;
    const doorOpenTimestamp = Date.parse(new Date())
    this.setState(
      {
        isAlertDialogOpen: false,
        doorToOpenId: controllerId,
        controllerUid,
        doorSuccess: false,
        doorFailure: false,
        doorWait: true,
        doorOpenTimestamp
      },
      () => this.props.openDoor(controllerId, this.props.customerId),
    );
  };

  render() {
    const isSmall = this.props.size.width < Metrics.tableWidth.small;
    const fullWidth = this.props.size.width - 40;
    const elementWidth = isSmall ? fullWidth : (this.props.size.width - 60) / 2;
    const { t, classes } = this.props;
    const { value } = this.state;
    const sitesData = this.props.sites.map((site) => ({ name: site.address.fullName, ...site }));
    const controllersInitialData = cloneDeep(this.props.controllers);
    const controllersData = controllersInitialData
      ? controllersInitialData.map((controller) => ({
        ...controller,
        nameDescription: `${controller.name ? controller.name : ''} ${controller.description ? controller.description : ''
          }`,
      }))
      : [];
    const filteredData1 = controllersData.filter((item) => {
      const name = item.nameDescription ? item.nameDescription.toLowerCase() : '';
      const searchValue = value ? value.toLowerCase() : '';
      return name.includes(searchValue);
    });
    const filteredData =
      this.state.doorWait || this.state.doorSuccess || this.state.doorFailure
        ? filteredData1.filter((door) => door.controllerId === this.state.doorToOpenId)
        : filteredData1;
    const defaultSiteId = sitesData.length === 1 ? sitesData[0]['siteId'] : this.state.siteId;

    return (
      <div className={classes.widgetContainer}>
        <AlertDialog
          open={this.state.isAlertDialogOpen}
          handleClose={() => this.setState({ isAlertDialogOpen: false })}
          title={this.state.alertTitle}
          contentText={this.state.alertContentText}
          cancelButtonText={t('Global_cancel')}
          confirmButtonText={t('home.openDoor')}
          onConfirm={this.state.alertConfirmAction}
        />
        <div className={classes.title}>
          <Typography variant="h2" className={classes.label}>
            {t('framesIndex_widget_controllerManualControl')}
          </Typography>
        </div>
        <div className={classes.selectorContainer}>
          <Select
            selectData={sitesData}
            selectLabel={t('reportManager_ControllersFilterTab_col1')}
            selectValue={defaultSiteId}
            onSelect={(siteId) => this.selectSite(siteId)}
            accessValue={'siteId'}
            accessLabel={'name'}
            selectWidth={elementWidth}
            disabled={!this.props.sites || this.props.sites.length <= 1}
            noEntry={t('SiteManager_searchCustomerSites_noSites')}
          />
          <SearchImmediate
            searchPlaceholder={t('doorControlManager_addControllersModal_searchControllers')}
            textFieldWidth={elementWidth}
            value={this.state.value}
            onKeyPress={this.handleChange}
            disabled={!defaultSiteId}
          />
        </div>

        <div
          className={clsx([
            classes.body,
            isSmall && classes.bodySmall,
            (this.state.doorSuccess || this.state.doorFailure) && classes.singleElementBody,
          ])}
        >
          {filteredData.map((door) => {
            const matchingStatus = this.state.doorStatuses.find(
              ({ controllerUid }) => controllerUid === door.controllerUid,
            );
            const { isDoorClosed, isLockClosed } = matchingStatus || {};
            const color = !isDoorClosed ? 'red' : !isLockClosed ? 'yellow' : 'green';
            return (
              <div
                className={clsx([
                  classes.door,
                  this.state.doorSuccess && classes.doorSuccess,
                  this.state.doorFailure && classes.doorFailure,
                  !this.state.doorWait && !this.state.doorSuccess && !this.state.doorFailure && classes[color],
                ])}
                key={door.controllerId}
                style={{
                  width:
                    this.state.doorWait || this.state.doorSuccess || this.state.doorFailure ? fullWidth : elementWidth,
                }}
              >
                <Tooltip title={door.nameDescription}>
                  <div className={classes.doorNameText}>{door.nameDescription}</div>
                </Tooltip>
                {this.state.doorWait ? (
                  <p className={classes.statusText}>
                    {t('widgetControllerManualControl_wait')}
                    {'...'} {this.state.timer < 10 && <span>&nbsp;&nbsp;</span>}
                    {this.state.timer}
                  </p>
                ) : this.state.doorSuccess ? (
                  <>
                    <p className={classes.statusText}>{t('widgetControllerManualControl_success')}</p>{' '}
                    <SvgIcon icon="Save" width={20} color={Colors.white} />
                  </>
                ) : this.state.doorFailure ? (
                  <>
                    <p className={classes.statusText}>{t('unexpectedError')}</p> <ErrorOutlineIcon width={20} />
                  </>
                ) : (
                  <Tooltip title={t('home.openDoor')}>
                    <IconButton
                      edge="end"
                      onClick={() => this.openOpenDoorAlert(door.controllerId)}
                      disabled={this.state.doorWait}
                      aria-label="open door"
                    >
                      <SvgIcon
                        icon="Lock"
                        width={20}
                        color={this.state.doorWait ? Colors.disabledGrey : Colors.orange}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            );
          })}
        </div>
        {(this.state.doorSuccess || this.state.doorFailure) && (
          <Button
            onClick={() => this.setState({ doorSuccess: false, doorFailure: false, doorToOpenId: '' })}
            className={classes.okButton}
          >
            {t('Global_ok')}
          </Button>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    distributorId: state.auth.distributorId,
    customerId: state.auth.customerId,
    sites: state.sites.allSites,
    controllers: state.sites.controllers,
    commandId: state.openDoor.commandId,
    isDoorOpening: state.openDoor.isDoorOpening,
    doorStatus: state.openDoor.doorStatus,
    // webSocket
    webSocketEvents: state.eventReports.webSocketEvents,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getAllSites: (customerId, pageParams) => dispatch(SitesActions.getAllSitesRequest(customerId, pageParams)),
  getSiteControllers: (siteId) => dispatch(SitesActions.getSiteControllersRequest(siteId)),
  resetSites: () => dispatch(SitesActions.resetSites()),
  resetControllers: () => dispatch(SitesActions.resetControllers()),
  openDoor: (controllerId, customerId) => dispatch(OpenDoorActions.openDoorRequest(controllerId, customerId)),
  checkDoorStatus: (controllerId, commandId) =>
    dispatch(OpenDoorActions.checkDoorStatusRequest(controllerId, commandId)),
  resetStatus: () => dispatch(OpenDoorActions.resetStatus()),
  clearWebSocketEvents: () => dispatch(EventReportsActions.clearWebSocketEvents()),
  // Web socket sites subscription
  updateSitesQueues: (sites) => dispatch(WebSocketActions.updateSitesQueues(sites)),
});

export default compose(
  withTranslation(),
  withStyles(styles),
  withSize(),
  connect(mapStateToProps, mapDispatchToProps),
)(DoorManualControl);
